import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { NavLink, useMatch } from 'react-router-dom';
import { usePrevious } from 'react-use';

import './CelestialRoseNavItem.scss';

const SCROLL_OFFSET = 75;

type CelestialRoseNavItemProps = {
    id: string;
    ownerName?: string;
    displayName: string;
    iconPath?: string;
};

const css = 'yat-artifacts__celestial-rose-nav-item';

export function CelestialRoseNavItem({ id, ownerName, displayName, iconPath }: CelestialRoseNavItemProps) {
    const { t } = useTranslation(['artifacts']);
    const ref = useRef<HTMLDivElement>(null);
    const routeMatch = useMatch({
        path: `/artifacts/celestial-rose/${id}`,
        end: true,
    });
    const isMatchingRoute = Boolean(routeMatch);
    const prevIsMatchingRoute = usePrevious(isMatchingRoute);
    const [isIconLoaded, setIsIconLoaded] = useState(false);

    // scroll to self when route matches
    useEffect(() => {
        if (isMatchingRoute && !prevIsMatchingRoute) {
            const navItemEl = ref.current;
            const scrollParentEl = navItemEl?.parentElement;
            const scrollParentTop = navItemEl?.parentElement?.getBoundingClientRect().top || 0;
            const navItemTop = navItemEl?.getBoundingClientRect().top || 0;
            scrollParentEl?.scrollTo({
                top: navItemTop - scrollParentTop + scrollParentEl?.scrollTop - SCROLL_OFFSET,
                behavior: 'smooth',
            });
        }
    }, [isMatchingRoute, prevIsMatchingRoute]);

    const handleIconLoad = useCallback(() => {
        setIsIconLoaded(true);
    }, [setIsIconLoaded]);

    return (
        <div className={css} ref={ref}>
            <NavLink
                className={({ isActive }) => {
                    const baseClassNames = [`${css}__link`];
                    if (isActive) {
                        baseClassNames.push(`${css}__link--active`);
                    }
                    return baseClassNames.join(' ');
                }}
                to={`/artifacts/celestial-rose/${id}`}
            >
                <img
                    className={`${css}__icon ${isIconLoaded ? `${css}__icon--loaded` : ''}`}
                    src={iconPath}
                    alt={displayName}
                    onLoad={handleIconLoad}
                />
                <div className={`${css}__content`}>
                    <h3 className={`${css}__name`}>{displayName}</h3>
                    {ownerName && (
                        <h4 className={`${css}__owner`}>
                            {t('held_by')} {ownerName}
                        </h4>
                    )}
                </div>
            </NavLink>
        </div>
    );
}
